import React, { useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import { useTranslation } from 'next-i18next';
import { useForm } from 'react-hook-form';
import styles from './ContactTravelTradeForm.module.scss';
import ButtonPrimary from '../ButtonPrimary';
import classNames from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import TextareaAutosize from 'react-textarea-autosize';
import { Dialog, DialogDismiss, DialogHeading, useDialogState } from 'ariakit/dialog';

const ContactTravelTradeForm = ({ formProps, csrf }) => {
    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: { contact: 'email' },
    });

    const dialog = useDialogState({ animated: true });
    const [ref, isVisible] = useIntersectionObserver();
    const { t } = useTranslation();
    const hasStockholmRadio = watch('hasStockholmInPortfolio');
    const planningToAddStockholm = watch('planningToAddStockholm');

    const { executeRecaptcha } = useGoogleReCaptcha();
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!dialog.open) reset();
    }, [dialog.open, reset]);

    const URL = '/wt/api/nextjs/v1/send_contact_form/?type=traveltrade';

    const getFreshToken = async () => {
        if (!executeRecaptcha) throw new Error('reCAPTCHA not ready');
        const token = await executeRecaptcha('traveltrade_form');
        if (!token) throw new Error('Failed to get reCAPTCHA token');
        return token;
    };

    const postOnce = async (data) => {
        const token = await getFreshToken();

        const yn = (v) => (v === true || v === 'yes' || v === '1' ? 'yes' : 'no');

        const formData = new FormData();
        formData.append('recaptcha_token', token);
        formData.append('first_name', data.firstName);
        formData.append('last_name', data.lastName);
        formData.append('title', data.title);
        formData.append('email', data.email);
        formData.append('phonenumber', data.phonenumber);
        formData.append('company', data.company);
        formData.append('country', data.country);
        formData.append('website', data.website);
        formData.append('arrival_date', data.arrivalDate);
        formData.append('number_of_days', data.numberOfDays);
        formData.append('hotel_name', data.hotelName);

        const hasSthlm = yn(data.hasStockholmInPortfolio);
        formData.append('has_stockholm_in_portfolio', hasSthlm);

        const planning = hasSthlm === 'yes' ? 'no' : yn(data.planningToAddStockholm);
        formData.append('planning_to_add_stockholm', planning);

        formData.append('message', data.message ?? '');

        const res = await fetch(URL, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'X-CSRFToken': csrf,
            },
            body: formData,
            credentials: 'include',
        });

        let parsed = null;
        try {
            parsed = await res.clone().json();
        } catch (_) {}

        return { res, parsed };
    };

    const handleSubmitForm = async (data) => {
        if (submitting) return;
        setSubmitting(true);
        try {
            let { res, parsed } = await postOnce(data);

            if (res.status === 401 && Array.isArray(parsed?.recaptcha_error_codes)) {
                if (parsed.recaptcha_error_codes.includes('timeout-or-duplicate')) {
                    ({ res, parsed } = await postOnce(data));
                }
            }

            if (!res.ok) {
                const msg = parsed?.message || `${res.status} ${res.statusText}: Failed to submit form`;
                console.error('Submit failed', { status: res.status, body: parsed });
                throw new Error(msg);
            }

            dialog.show();
        } catch (error) {
            console.error('Error:', error?.message || error);
        } finally {
            setSubmitting(false);
        }
    };

    const formClasses = classNames(styles['Root'], 'black-glass', {
        [styles['Visible']]: isVisible,
    });

    return (
        <>
            <form onSubmit={handleSubmit(handleSubmitForm)} noValidate ref={ref} className={formClasses}>
                <div className={styles['FieldsetWrapper']}>
                    <p className={styles['Description']}>{t('ContactForm.description')}</p>
                    <fieldset className={styles['Fieldset']}>
                        <legend className={styles['Legend']}>{t('ContactPressForm.legends.contactInfo')}</legend>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('firstName'),
                                    [styles['HasError']]: errors.firstName,
                                })}>
                                <input
                                    id="firstName"
                                    className={styles['Input']}
                                    autoComplete="given-name"
                                    aria-invalid={!!errors.firstName}
                                    aria-describedby={errors.firstName ? 'firstName-error' : null}
                                    maxLength={70}
                                    {...register('firstName', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="firstName">
                                    {t('ContactForm.labels.firstName')} *
                                </label>
                            </div>
                            {errors.firstName && (
                                <ErrorMessage id="firstName-error">This field is required</ErrorMessage>
                            )}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('lastName'),
                                    [styles['HasError']]: errors.lastName,
                                })}>
                                <input
                                    id="lastName"
                                    className={styles['Input']}
                                    autoComplete="family-name"
                                    aria-invalid={!!errors.lastName}
                                    aria-describedby={errors.lastName ? 'lastName-error' : null}
                                    maxLength={70}
                                    {...register('lastName', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="lastName">
                                    {t('ContactForm.labels.lastName')} *
                                </label>
                            </div>
                            {errors.lastName && <ErrorMessage id="lastName-error">This field is required</ErrorMessage>}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('title'),
                                    [styles['HasError']]: errors.title,
                                })}>
                                <input
                                    id="title"
                                    className={styles['Input']}
                                    autoComplete="organization-title"
                                    aria-invalid={!!errors.title}
                                    aria-describedby={errors.title ? 'title-error' : null}
                                    maxLength={70}
                                    {...register('title', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="title">
                                    {t('ContactForm.labels.title')} *
                                </label>
                            </div>
                            {errors.title && <ErrorMessage id="title-error">This field is required</ErrorMessage>}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('email'),
                                    [styles['HasError']]: errors.email,
                                })}>
                                <input
                                    id="email"
                                    className={styles['Input']}
                                    autoComplete="email"
                                    maxLength={150}
                                    aria-invalid={!!errors.email}
                                    aria-describedby={errors.email ? 'email-error' : null}
                                    {...register('email', {
                                        required: t('ContactForm.errors.required'),
                                        pattern: {
                                            value: /\S+@\S+\.\S+/,
                                            message: t('ContactForm.errors.validEmail'),
                                        },
                                    })}
                                />
                                <label className={styles['Label']} htmlFor="email">
                                    {t('ContactForm.labels.email')} *
                                </label>
                            </div>
                            {errors.email && <ErrorMessage id="email-error">{errors.email.message}</ErrorMessage>}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('phonenumber'),
                                    [styles['HasError']]: errors.phonenumber,
                                })}>
                                <input
                                    className={styles['Input']}
                                    id="phonenumber"
                                    maxLength={30}
                                    autoComplete="tel"
                                    aria-labelledby="phoneNum phoneHelp"
                                    aria-describedby="phonenumber-error"
                                    {...register('phonenumber', { required: true })}
                                />
                                <label id="phoneNum" className={styles['Label']} htmlFor="phonenumber">
                                    {t('ContactForm.labels.phone')} *
                                </label>
                            </div>
                            <div id="phoneHelp" className={styles['HelpText']}>
                                {t('ContactForm.help.phone')}
                            </div>
                            {errors.phonenumber && (
                                <ErrorMessage id="phonenumber-error">{t('ContactForm.errors.required')}</ErrorMessage>
                            )}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('company'),
                                    [styles['HasError']]: errors.company,
                                })}>
                                <input
                                    id="company"
                                    className={styles['Input']}
                                    autoComplete="organization"
                                    aria-invalid={!!errors.company}
                                    aria-describedby={errors.company ? 'company-error' : null}
                                    maxLength={70}
                                    {...register('company', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="company">
                                    {t('ContactForm.labels.company')} *
                                </label>
                            </div>
                            {errors.company && <ErrorMessage id="company-error">This field is required</ErrorMessage>}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('country'),
                                    [styles['HasError']]: errors.country,
                                })}>
                                <input
                                    id="country"
                                    className={styles['Input']}
                                    autoComplete="country-name"
                                    aria-invalid={!!errors.country}
                                    aria-describedby={errors.country ? 'country-error' : null}
                                    maxLength={70}
                                    {...register('country', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="country">
                                    {t('ContactForm.labels.country')} *
                                </label>
                            </div>
                            {errors.country && <ErrorMessage id="country-error">This field is required</ErrorMessage>}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('website'),
                                    [styles['HasError']]: errors.website,
                                })}>
                                <input
                                    id="website"
                                    className={styles['Input']}
                                    autoComplete="url"
                                    aria-invalid={!!errors.website}
                                    aria-describedby={errors.website ? 'website-error' : null}
                                    maxLength={150}
                                    {...register('website', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="website">
                                    {t('ContactForm.labels.website')} *
                                </label>
                                {errors.website && (
                                    <ErrorMessage id="website-error">{t('ContactForm.errors.required')}</ErrorMessage>
                                )}
                            </div>
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('arrivalDate'),
                                    [styles['HasError']]: errors.arrivalDate,
                                })}>
                                <input
                                    id="arrivalDate"
                                    className={styles['Input']}
                                    autoComplete="off"
                                    aria-invalid={!!errors.arrivalDate}
                                    aria-describedby={errors.arrivalDate ? 'arrivalDate-error' : null}
                                    maxLength={150}
                                    {...register('arrivalDate', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="arrivalDate">
                                    {t('ContactForm.labels.arrivalDate')} *
                                </label>
                                {errors.arrivalDate && (
                                    <ErrorMessage id="arrivalDate-error">
                                        {t('ContactForm.errors.required')}
                                    </ErrorMessage>
                                )}
                            </div>
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('numberOfDays'),
                                    [styles['HasError']]: errors.numberOfDays,
                                })}>
                                <input
                                    id="numberOfDays"
                                    className={styles['Input']}
                                    autoComplete="off"
                                    aria-invalid={!!errors.numberOfDays}
                                    aria-describedby={errors.numberOfDays ? 'numberOfDays-error' : null}
                                    maxLength={150}
                                    {...register('numberOfDays', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="numberOfDays">
                                    {t('ContactForm.labels.numberOfDays')} *
                                </label>
                                {errors.numberOfDays && (
                                    <ErrorMessage id="numberOfDays-error">
                                        {t('ContactForm.errors.required')}
                                    </ErrorMessage>
                                )}
                            </div>
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('hotelName'),
                                    [styles['HasError']]: errors.hotelName,
                                })}>
                                <input
                                    id="hotelName"
                                    className={styles['Input']}
                                    autoComplete="off"
                                    aria-invalid={!!errors.hotelName}
                                    aria-describedby={errors.hotelName ? 'hotelName-error' : null}
                                    maxLength={150}
                                    {...register('hotelName', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="hotelName">
                                    {t('ContactForm.labels.hotelName')} *
                                </label>
                                {errors.hotelName && (
                                    <ErrorMessage id="hotelName-error">{t('ContactForm.errors.required')}</ErrorMessage>
                                )}
                            </div>
                        </div>
                    </fieldset>

                    <div className={styles['FieldsetWrapper']}>
                        <fieldset className={styles['Fieldset']}>
                            <legend className={styles['Legend']}>
                                {t('ContactForm.legends.stockholmInPortfolio')}
                            </legend>
                            <div className={styles['RadioGroupWrapper']}>
                                <div className={styles['RadioWrapper']}>
                                    <input
                                        {...register('hasStockholmInPortfolio', { required: true })}
                                        type="radio"
                                        value="yes"
                                        className={styles['Radio']}
                                        id="yes"
                                    />
                                    <label className={styles['RadioLabel']} htmlFor="yes">
                                        {hasStockholmRadio === 'yes' && (
                                            <Image
                                                width={12}
                                                height={20}
                                                src={`/img/icon-check.svg`}
                                                alt=""
                                                className={styles['Check']}
                                            />
                                        )}
                                        {t('ContactForm.labels.yes')}
                                    </label>
                                </div>
                                <div className={styles['RadioWrapper']}>
                                    <input
                                        {...register('hasStockholmInPortfolio', { required: true })}
                                        type="radio"
                                        value="no"
                                        className={styles['Radio']}
                                        id="no"
                                    />
                                    <label className={styles['RadioLabel']} htmlFor="no">
                                        {hasStockholmRadio === 'no' && (
                                            <Image
                                                width={12}
                                                height={20}
                                                src={`/img/icon-check.svg`}
                                                alt=""
                                                className={styles['Check']}
                                            />
                                        )}
                                        {t('ContactForm.labels.no')}
                                    </label>
                                </div>
                            </div>
                            {errors.hasStockholmInPortfolio && (
                                <div>
                                    <ErrorMessage>This field is required</ErrorMessage>
                                </div>
                            )}
                        </fieldset>

                        {hasStockholmRadio === 'no' && (
                            <fieldset className={styles['Fieldset']}>
                                <legend className={styles['Legend']}>
                                    {t('ContactForm.legends.planningToAddStockholm')}
                                </legend>
                                <div className={styles['RadioGroupWrapper']}>
                                    <div className={styles['RadioWrapper']}>
                                        <input
                                            {...register('planningToAddStockholm', {
                                                required: true,
                                            })}
                                            type="radio"
                                            value="yes"
                                            className={styles['Radio']}
                                            id="planningYes"
                                        />
                                        <label className={styles['RadioLabel']} htmlFor="planningYes">
                                            {planningToAddStockholm === 'yes' && (
                                                <Image
                                                    width={12}
                                                    height={20}
                                                    src={`/img/icon-check.svg`}
                                                    alt=""
                                                    className={styles['Check']}
                                                />
                                            )}
                                            {t('ContactForm.labels.yes')}
                                        </label>
                                    </div>
                                    <div className={styles['RadioWrapper']}>
                                        <input
                                            {...register('planningToAddStockholm', {
                                                required: true,
                                            })}
                                            type="radio"
                                            value="no"
                                            className={styles['Radio']}
                                            id="planningNo"
                                        />
                                        <label className={styles['RadioLabel']} htmlFor="planningNo">
                                            {planningToAddStockholm === 'no' && (
                                                <Image
                                                    width={12}
                                                    height={20}
                                                    src={`/img/icon-check.svg`}
                                                    alt=""
                                                    className={styles['Check']}
                                                />
                                            )}
                                            {t('ContactForm.labels.no')}
                                        </label>
                                    </div>
                                </div>
                                {errors.planningToAddStockholm && <ErrorMessage>This field is required</ErrorMessage>}
                            </fieldset>
                        )}
                    </div>

                    <div className={styles['FieldsetWrapper']}>
                        <fieldset className={styles['Fieldset']}>
                            <div
                                className={classNames(styles['Field'], styles['TextareaField'], {
                                    [styles['HasValue']]: watch('message'),
                                })}>
                                <TextareaAutosize
                                    className={styles['Textarea']}
                                    id="message"
                                    {...register('message')}
                                />
                                <label className={styles['Label']} htmlFor="message">
                                    {t('ContactForm.labels.messagePlans')}
                                </label>
                            </div>
                        </fieldset>
                    </div>
                </div>

                <div className={styles['PrivacyWrapper']}>
                    <input
                        type="checkbox"
                        className={styles['Checkbox']}
                        name="acceptPrivacy"
                        id="acceptPrivacy"
                        aria-invalid={!!errors.acceptPrivacy}
                        aria-describedby={errors.acceptPrivacy ? 'privacy-error' : null}
                        {...register('acceptPrivacy', { required: true })}
                    />
                    <label
                        dangerouslySetInnerHTML={{
                            __html: formProps?.privacyPolicy?.label,
                        }}
                        className={styles['CheckboxLabel']}
                        htmlFor="acceptPrivacy"
                    />
                </div>
                {errors.acceptPrivacy && <ErrorMessage id="privacy-error">This field is required</ErrorMessage>}

                {!!errors && Object.keys(errors).length > 0 && (
                    <div className={styles['ErrorListWrapper']} role="alert">
                        <ul className={styles['ErrorList']}>
                            {Object.keys(errors).map((key) => (
                                <li key={key} className={styles['ErrorListItem']}>
                                    <Image
                                        width={16}
                                        height={16}
                                        src={`/img/icon-warning.svg`}
                                        alt=""
                                        className={styles['Warning']}
                                    />
                                    <Link href={`#${key}`} className={styles['LinkError']}>
                                        {t(`ContactPressForm.errors.${key}`)}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className={styles['ButtonWrapper']}>
                    <ButtonPrimary
                        type="submit"
                        icon={'icon-send'}
                        modifiers={['InlineFlex', 'CenterText', 'NarrowIconGap', 'BigButton']}
                        disabled={submitting}>
                        {t('ContactPressForm.labels.send')}
                    </ButtonPrimary>
                </div>

                <small className={styles['ReCaptchaDisclaimer']}>
                    {t('ContactForm.recaptcha.protectedBy')}
                    <a href="https://policies.google.com/privacy">{t('ContactForm.recaptcha.privacyPolicy')}</a>
                    {t('ContactForm.recaptcha.and')}
                    <a href="https://policies.google.com/terms">{t('ContactForm.recaptcha.termsOfService')}</a>
                    {t('ContactForm.recaptcha.apply')}
                </small>
            </form>

            <Dialog
                backdropProps={{ className: styles['Backdrop'] }}
                state={dialog}
                className={`${styles['Dialog']} black-glass`}>
                <DialogHeading className={styles['DialogHeader']}>
                    {t('ContactPressForm.dialog.headline')}
                </DialogHeading>
                <div className={`${styles['DialogContainer']}`}>{t('ContactPressForm.dialog.body')}</div>
                <div className={`${styles['ButtonContainer']}`}>
                    <ButtonPrimary modifiers={['TextAlignCenter']} onClick={dialog.close} asLink={true} href="/">
                        {t('ContactPressForm.dialog.button')}
                    </ButtonPrimary>
                </div>
                <DialogDismiss className={styles['Dismiss']}>
                    <span className="sr-only">{t('ContactPressForm.dialog.button')}</span>
                </DialogDismiss>
            </Dialog>
        </>
    );
};

const ErrorMessage = ({ children, id }) => {
    return (
        <div id={id} className={styles['Error']}>
            <Image width={16} height={16} src={`/img/icon-warning.svg`} alt="" className={styles['Warning']} />
            {children}
        </div>
    );
};

export default ContactTravelTradeForm;
